/* eslint-disable @typescript-eslint/no-unused-vars */
import { Logger } from './logger.service';
import { JitsiConferenceEvents, JitsiConnectionEvents } from 'nexgen-types/lib-jitsi';
import { getJitsiConnectionConfig } from '../utils/jitsi/helper-methods';
import {
  JITSI_MEET_CONFIG,
  JITSI_OPTIMAL_AUDIO_CONSTRAINTS,
  JITSI_OPTIMAL_BITRATE_CONFIG,
} from '../../utils/constants';
import { jitsiService } from '../singletons/jitsi-singleton';
import { handleAsync } from '../../utils';
import { ApiService } from './api.service';
import { useMeetingStore, useNotificationStore } from '../../host/stores';
import { getParticipantIdByTrack } from '../../host/stores/utils/meeting-store-utils';
import { MeetingTrackType } from '../interfaces/meeting-service.interface';
import { useSessionStore } from '../../host/stores/session.store';

export declare let JitsiMeetJS: any;

interface WhisperParticipant {
  id: string;
  isLocal: boolean;
  isMuted: boolean;
  audioTrack?: any;
}

interface WhisperConnectionConfig {
  roomId: string;
  token?: string;
}

export class JitsiWhisperService {
  private connection: any = null;
  private conference: any = null;
  private localAudioTrack: any = null;
  private conferenceOptions: any;
  private conferenceConfig: WhisperConnectionConfig | null = null;
  private setWhisperSessionStatus = useMeetingStore.getState().setWhisperSessionStatus;

  constructor(conferenceOptions: any = { openBridgeChannel: true, p2p: { enabled: false } }) {
    try {
      this.conferenceOptions = conferenceOptions;
      JitsiMeetJS.setLogLevel(JitsiMeetJS.logLevels.ERROR);
      JitsiMeetJS.init(JITSI_MEET_CONFIG);
      Logger.debug('[JitsiWhisperService] Initialized');
    } catch (error) {
      Logger.error('[JitsiWhisperService] Failed to initialize JitsiMeetJS:', error);
    }
  }

  async connect(roomId: string, token?: string): Promise<void> {
    try {
      Logger.debug('[JitsiWhisperService] Connecting to room:', roomId);
      const connectionConfig = getJitsiConnectionConfig(roomId);
      this.conferenceConfig = { roomId, token };
      this.connection = new JitsiMeetJS.JitsiConnection(null, token, connectionConfig);
      this.setupConnectionListeners();
      await this.connection.connect();
    } catch (error) {
      this.setWhisperSessionStatus('error');
      Logger.error('[JitsiWhisperService] Failed to connect:', error);
      throw error;
    }
  }

  joinWhisperingRoom = async (signalingPayload: any) => {
    this.setWhisperSessionStatus('connecting');

    const { to, from } = signalingPayload;
    const sessionId = useSessionStore.getState().getOrCreateSessionId();
    const [err, result] = await handleAsync(() =>
      ApiService.request<any>({
        method: 'GET',
        endpoint: `meeting/whisper_token`,
        data: { to_user_id: to, from_user_id: from, session_id: sessionId },
      })
    );

    if (err || !result) {
      this.setWhisperSessionStatus('error');
      Logger.error('[joinWhisperingRoom] ❌ Error getting whisper room token:', err);
      return;
    }

    await this.connect(result.roomId, result.token);
  };

  getSelfParticipantId() {
    return this.conference?.myUserId();
  }

  async join(config: WhisperConnectionConfig): Promise<void> {
    try {
      Logger.debug('[JitsiWhisperService] Joining conference:', config.roomId);

      if (!this.connection) {
        throw new Error('Not connected. Call connect() first.');
      }

      if (config.roomId === this.conference?.getName()) {
        Logger.debug('[JitsiWhisperService] Already in this conference, skipping join');
        return;
      }

      this.conference = this.connection.initJitsiConference(config.roomId, this.conferenceOptions);
      this.setupConferenceListeners();
      await this.createLocalAudioTrack();
      await this.conference.join();

      if (this.localAudioTrack) {
        await this.conference.addTrack(this.localAudioTrack);
      }
    } catch (error) {
      Logger.error('[JitsiWhisperService] Failed to join conference:', error);
      throw error;
    }
  }

  async leave(killConnection: boolean = true): Promise<void> {
    const { setWhisperSessionStatus } = useMeetingStore.getState();

    try {
      if (this.localAudioTrack) {
        try {
          this.localAudioTrack.detach();
          const stream = this.localAudioTrack.getOriginalStream?.();
          if (stream) {
            for (const track of stream.getTracks()) {
              track.stop();
            }
          }
          const nativeTrack = this.localAudioTrack.getTrack?.();
          if (nativeTrack && nativeTrack.readyState === 'live') {
            nativeTrack.stop();
          }
          await this.localAudioTrack.dispose();
        } catch (error) {
          Logger.error('[JitsiWhisperService] Error disposing local track:', error);
        }
        this.localAudioTrack = null;
      }

      if (this.conference) {
        try {
          this.removeConferenceListeners();
          await this.conference.leave();
        } catch (error) {
          Logger.error('[JitsiWhisperService] Error leaving conference:', error);
        } finally {
          this.conference = null;
        }
      }

      if (this.connection && killConnection) {
        try {
          this.removeConnectionListeners();
          this.connection.disconnect();
        } catch (error) {
          Logger.error('[JitsiWhisperService] Error disconnecting:', error);
        } finally {
          this.connection = null;
          useNotificationStore
            .getState()
            .showSnackbar('Ended whispering to the participant', 'success');
        }

        useMeetingStore.setState({ whisperMeetingInfo: null, whisperRoomParticipants: {} });
      }
      Logger.debug('[JitsiWhisperService] Left conference');

      setWhisperSessionStatus('disconnected');
    } catch (error) {
      setWhisperSessionStatus('error');
      useMeetingStore.setState({ whisperMeetingInfo: null, whisperRoomParticipants: {} });
      Logger.error('[JitsiWhisperService] Error during leave:', error);
      throw error;
    }
  }

  private async createLocalAudioTrack(): Promise<void> {
    try {
      const trackOptions = {
        devices: ['audio'],
        constraints: {
          audio: { ...JITSI_OPTIMAL_AUDIO_CONSTRAINTS },
        },
        ...JITSI_OPTIMAL_BITRATE_CONFIG,
      };

      const [audioTrack] = await JitsiMeetJS.createLocalTracks(trackOptions);
      this.localAudioTrack = audioTrack;

      Logger.debug('[JitsiWhisperService] Local audio track created');
    } catch (error) {
      Logger.error('[JitsiWhisperService] Failed to create local audio track:', error);
      throw error;
    }
  }

  async mute(): Promise<void> {
    if (this.localAudioTrack && !this.localAudioTrack.isMuted()) {
      await this.localAudioTrack.mute();
      Logger.debug('[JitsiWhisperService] Muted local audio');
    }
  }

  async unmute(): Promise<void> {
    if (this.localAudioTrack && this.localAudioTrack.isMuted()) {
      await this.localAudioTrack.unmute();
      Logger.debug('[JitsiWhisperService] Unmuted local audio');
    }
  }

  async toggleMute(): Promise<void> {
    if (this.isLocalMuted()) {
      await this.unmute();
    } else {
      await this.mute();
    }
  }

  async sendWhisperSignallingMessage(recipientId: string): Promise<void> {
    const message = {
      mode: 'whisper-signalling-request',
      from: jitsiService?.getMyUserId(),
      to: recipientId,
      timestamp: Date.now(),
      type: 'chat',
    };

    if (jitsiService) {
      useNotificationStore
        .getState()
        .showSnackbar('Whisper channel will start in few seconds...', 'info');

      await jitsiService.sendMessageToParticipant(JSON.stringify(message), recipientId);
      Logger.debug(
        '[JitsiWhisperService] Sent whisper signalling message to participant:',
        recipientId
      );
    }
  }

  async acknowledgeWhisperSignalling(whisperSessionInfo: any): Promise<void> {
    if (!whisperSessionInfo) return;

    const senderId = whisperSessionInfo?.from;
    const message = {
      mode: 'whisper-signalling-ack',
      from: senderId,
      to: jitsiService?.getMyUserId(),
      timestamp: Date.now(),
      type: 'chat',
    };

    if (jitsiService) {
      await jitsiService.sendMessageToParticipant(JSON.stringify(message), senderId);
      Logger.debug(
        '[JitsiWhisperService] Sent whisper acknowledgement message to participant:',
        senderId
      );
    }
  }

  async rejectWhisperSignalling(whisperSessionInfo: any): Promise<void> {
    const senderId = whisperSessionInfo?.from;
    const message = {
      mode: 'whisper-signalling-reject',
      from: senderId,
      reason: 'BUSY',
      to: jitsiService?.getMyUserId(),
      timestamp: Date.now(),
      type: 'chat',
    };

    if (jitsiService) {
      await jitsiService.sendMessageToParticipant(JSON.stringify(message), senderId);
      Logger.debug(
        '[JitsiWhisperService] Sent whisper acknowledgement message to participant:',
        senderId
      );
    }
  }

  isLocalMuted(): boolean {
    return this.localAudioTrack?.isMuted() ?? true;
  }

  getMyUserId(): string | null {
    return this.conference?.myUserId() ?? null;
  }

  getParticipantById(participantId: string): WhisperParticipant | null {
    const myId = this.getMyUserId();
    if (myId !== null && participantId === myId) {
      return {
        id: myId,
        isLocal: true,
        isMuted: this.isLocalMuted(),
        audioTrack: this.localAudioTrack,
      };
    }

    const { whisperRoomParticipants = {} } = useMeetingStore.getState();
    return whisperRoomParticipants[participantId] || null;
  }

  attachAudio(participantId: string, element: HTMLAudioElement): void {
    const participant = this.getParticipantById(participantId);
    if (participant?.audioTrack) {
      participant.audioTrack.attach(element);
      Logger.debug('[JitsiWhisperService] Attached audio for participant:', participantId);
    }
  }

  detachAudio(participantId: string, element: HTMLAudioElement): void {
    const participant = this.getParticipantById(participantId);
    if (participant?.audioTrack) {
      participant.audioTrack.detach(element);
      Logger.debug('[JitsiWhisperService] Detached audio for participant:', participantId);
    }
  }

  async destroy(): Promise<void> {
    await this.leave(true);
    Logger.debug('[JitsiWhisperService] Destroyed');
  }

  private setupConnectionListeners(): void {
    if (!this.connection) return;

    this.connection.addEventListener(
      JitsiConnectionEvents.CONNECTION_ESTABLISHED,
      this.handleConnectionEstablished
    );
    this.connection.addEventListener(
      JitsiConnectionEvents.CONNECTION_FAILED,
      this.handleConnectionFailed
    );
    this.connection.addEventListener(
      JitsiConnectionEvents.CONNECTION_DISCONNECTED,
      this.handleConnectionDisconnected
    );
  }

  private removeConnectionListeners(): void {
    if (!this.connection) return;

    this.connection.removeEventListener(
      JitsiConnectionEvents.CONNECTION_ESTABLISHED,
      this.handleConnectionEstablished
    );
    this.connection.removeEventListener(
      JitsiConnectionEvents.CONNECTION_FAILED,
      this.handleConnectionFailed
    );
    this.connection.removeEventListener(
      JitsiConnectionEvents.CONNECTION_DISCONNECTED,
      this.handleConnectionDisconnected
    );
  }

  private setupConferenceListeners(): void {
    if (!this.conference) return;

    this.conference.on(JitsiConferenceEvents.CONFERENCE_JOINED, this.handleConferenceJoined);
    this.conference.on(JitsiConferenceEvents.CONFERENCE_ERROR, this.handleConferenceError);
    this.conference.on(JitsiConferenceEvents.USER_JOINED, this.handleUserJoined);
    this.conference.on(JitsiConferenceEvents.USER_LEFT, this.handleUserLeft);
    this.conference.on(JitsiConferenceEvents.TRACK_ADDED, this.handleTrackAdded);
    this.conference.on(JitsiConferenceEvents.TRACK_REMOVED, this.handleTrackRemoved);
  }

  private removeConferenceListeners(): void {
    if (!this.conference) return;

    this.conference.off(JitsiConferenceEvents.CONFERENCE_JOINED, this.handleConferenceJoined);
    this.conference.off(JitsiConferenceEvents.CONFERENCE_ERROR, this.handleConferenceError);
    this.conference.off(JitsiConferenceEvents.USER_JOINED, this.handleUserJoined);
    this.conference.off(JitsiConferenceEvents.USER_LEFT, this.handleUserLeft);
    this.conference.off(JitsiConferenceEvents.TRACK_ADDED, this.handleTrackAdded);
    this.conference.off(JitsiConferenceEvents.TRACK_REMOVED, this.handleTrackRemoved);
  }

  private handleConnectionEstablished = (): void => {
    if (this.conferenceConfig) this.join(this.conferenceConfig);
    Logger.debug('[JitsiWhisperService] Connection established');
  };

  private handleConnectionFailed = (error: any): void => {
    Logger.error('[JitsiWhisperService] Connection failed:', error);
  };

  private handleConnectionDisconnected = (): void => {
    useMeetingStore.setState({ whisperMeetingInfo: null, whisperRoomParticipants: {} });
    Logger.debug('[JitsiWhisperService] Connection disconnected');
  };

  private handleConferenceJoined = (): void => {
    Logger.debug('[JitsiWhisperService] Conference joined');
    jitsiService?.toggleTrack(MeetingTrackType.AUDIO, true, jitsiService?.getMyUserId());

    setTimeout(async () => {
      const participants = useMeetingStore.getState().whisperRoomParticipants || {};
      const participantCount = Object.keys(participants).length;

      if (participantCount <= 1) {
        Logger.debug(
          '[JitsiWhisperService] No other participants after 10s. Leaving whisper room...'
        );
        this.leave(true);
        if (!this.conference) return;
        useNotificationStore
          .getState()
          .showSnackbar('Attempt to whisper failed, try again!', 'warning');
      }
    }, 10000);
  };

  private handleConferenceError = (error: any): void => {
    Logger.error('[JitsiWhisperService] Conference error:', error);
  };

  handleUserJoined = (participantId?: string, participant?: any) => {
    if (!participantId) return;

    useMeetingStore.setState((state) => {
      if ((state.whisperRoomParticipants ?? {})[participantId]) {
        return {
          whisperRoomParticipants: {
            ...state.whisperRoomParticipants,
            [participantId]: (state.whisperRoomParticipants ?? {})[participantId],
          },
        };
      }

      return {
        whisperRoomParticipants: {
          ...state.whisperRoomParticipants,
          [participantId]: {
            id: participantId,
            isLocal: false,
            userPid: participant._identity?.user?.id,
          },
        },
      };
    });

    this.setWhisperSessionStatus('connected');
    useNotificationStore.getState().showSnackbar('Whispering is active now', 'success');
  };

  private handleUserLeft = (participantId?: string) => {
    if (!participantId) return;

    useMeetingStore.setState((state) => {
      const { [participantId]: _, ...rest } = state.whisperRoomParticipants || {};
      return { whisperRoomParticipants: rest };
    });

    if (Object.values(useMeetingStore.getState().whisperRoomParticipants || {}).length < 2) {
      this.setWhisperSessionStatus('disconnected');
      this.leave(true);
    }
  };

  private handleTrackAdded = (track: any) => {
    if (!track || !track.getParticipantId()) return;

    const participantId = track.getParticipantId();
    const trackId = track.stream.id;

    useMeetingStore.setState((state) => {
      const existingParticipant = (state.whisperRoomParticipants ?? {})[participantId] || {
        id: participantId,
        isLocal: track.isLocal(),
      };

      const updatedParticipant = { ...existingParticipant, track, trackId };

      return {
        whisperRoomParticipants: {
          ...state.whisperRoomParticipants,
          [participantId]: updatedParticipant,
        },
      };
    });
  };

  private handleTrackRemoved = (track: any) => {
    if (!track || !track.getParticipantId()) return;
    const participantId = getParticipantIdByTrack(track);

    useMeetingStore.setState((state) => {
      const participant = (state.whisperRoomParticipants || {})[participantId];
      if (!participant) return state;

      const { [participantId]: _, ...rest } = state.whisperRoomParticipants ?? {};
      return { whisperRoomParticipants: rest };
    });
  };
}
